import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import TrialBanner from './TrialBanner';
import SoporteForm from './SoporteForm';
import { sessionsAPI } from '../lib/api';
import api from '../lib/api';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    LayoutDashboard,
    MessageSquare,
    FileText,
    BookOpen,
    BarChart3,
    User,
    LogOut,
    Menu,
    X,
    ChevronRight,
    GraduationCap,
    Shield,
    LifeBuoy,
    Star,
} from 'lucide-react';

const ADMIN_EMAILS = [
    'alvaro.mardones.rivera@gmail.com',
    'acontistaeduca@gmail.com',
    'mariuerosima@gmail.com',
    'josesepulvedamorales@gmail.com',
    'sabino@fuix.cl',
];

const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/chat', label: 'Asistente IA', icon: MessageSquare },
    { path: '/tareas', label: 'Tareas', icon: BookOpen },
    { path: '/borradores', label: 'Borradores', icon: FileText },
    { path: '/rubricas', label: 'Rúbricas', icon: FileText },
    { path: '/progreso', label: 'Progreso', icon: BarChart3 },
    { path: '/activar-plan', label: '⭐ Activar Plan', icon: Star, highlight: true },
];

export const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);
    const [pendingWelcomes, setPendingWelcomes] = useState(0);
    const [openTickets, setOpenTickets] = useState(0);

    // Admins: refrescar contador de bienvenidas pendientes cada 60s y al cambiar de ruta
    useEffect(() => {
        if (!isAdmin) return undefined;
        let cancelled = false;
        const fetchPending = async () => {
            try {
                const { data } = await api.get('/admin/welcome/pending');
                if (!cancelled) setPendingWelcomes(data.total || 0);
            } catch (_) {
                /* silent: el sidebar no debe romper si el endpoint falla */
            }
        };
        fetchPending();
        const id = setInterval(fetchPending, 60_000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [isAdmin, location.pathname]);

    // Admins: refrescar contador de tickets de soporte abiertos cada 60s
    useEffect(() => {
        if (!isAdmin) return undefined;
        let cancelled = false;
        const fetchOpen = async () => {
            try {
                const { data } = await api.get('/admin/support/count-open');
                if (!cancelled) setOpenTickets(data.open || 0);
            } catch (_) {
                /* silent */
            }
        };
        fetchOpen();
        const id = setInterval(fetchOpen, 60_000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [isAdmin, location.pathname]);

    const handleLogout = async () => {
        try { await sessionsAPI.end(); } catch (_) { /* fail-safe */ }
        await logout();
        navigate('/login');
    };

    // beforeunload: navegador cierra pestaña → enviamos sessionsAPI.end best-effort
    useEffect(() => {
        const onBeforeUnload = () => {
            try {
                const backend = process.env.REACT_APP_BACKEND_URL;
                if (!backend) return;
                const url = `${backend}/api/sessions/end`;
                // navigator.sendBeacon usa la cookie session_token automáticamente
                // (para usuarios Google). Para JWT no enviará header Authorization,
                // pero el handleLogout explícito ya lo cubre.
                if (navigator.sendBeacon) {
                    const blob = new Blob([''], { type: 'application/json' });
                    navigator.sendBeacon(url, blob);
                }
            } catch (_) { /* best-effort */ }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    const getInitials = (name) => {
        if (!name) return 'U';
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    return (
        <div className="min-h-screen bg-slate-50/50">
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black/50 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={cn(
                    'fixed left-0 top-0 z-40 h-screen w-64 border-r border-slate-200 bg-white transition-transform lg:translate-x-0',
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                )}
            >
                <div className="flex h-full flex-col">
                    {/* Logo */}
                    <div className="flex h-16 items-center gap-3 border-b border-slate-200 px-6">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900">
                            <GraduationCap className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex flex-col leading-tight">
                            <span className="font-heading text-sm font-bold text-slate-900">Portafolio Digital Docente</span>
                            <span className="text-[10px] text-slate-500 tracking-wide">Acontista Educa</span>
                        </div>
                    </div>

                    {/* Navigation */}
                    <ScrollArea className="flex-1 px-3 py-4">
                        <nav className="space-y-1">
                            {navItems.map((item) => {
                                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                                const Icon = item.icon;

                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        onClick={() => setSidebarOpen(false)}
                                        className={cn(
                                            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                                            isActive
                                                ? 'bg-slate-100 text-slate-900'
                                                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                                        )}
                                    >
                                        <Icon className="h-5 w-5" />
                                        {item.label}
                                        {isActive && <ChevronRight className="ml-auto h-4 w-4" />}
                                    </Link>
                                );
                            })}

                            {/* Admin button - only for admin users */}
                            {isAdmin && (
                                <>
                                    <div className="my-3 border-t border-slate-200" />
                                    <Link
                                        to="/admin"
                                        onClick={() => setSidebarOpen(false)}
                                        className={cn(
                                            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors relative',
                                            location.pathname === '/admin'
                                                ? 'bg-slate-900 text-white'
                                                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                                        )}
                                        data-testid="admin-nav-btn"
                                    >
                                        <span className="relative inline-flex">
                                            <Shield className="h-5 w-5" />
                                            {pendingWelcomes > 0 && (
                                                <span
                                                    className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white animate-pulse"
                                                    data-testid="admin-welcome-pending-dot"
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </span>
                                        Administración
                                        {pendingWelcomes > 0 && (
                                            <span
                                                className={cn(
                                                    'ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold',
                                                    location.pathname === '/admin'
                                                        ? 'bg-rose-500 text-white'
                                                        : 'bg-rose-100 text-rose-700'
                                                )}
                                                data-testid="admin-welcome-pending-badge"
                                                title={`${pendingWelcomes} bienvenida(s) pendiente(s) de envío`}
                                            >
                                                {pendingWelcomes > 99 ? '99+' : pendingWelcomes}
                                            </span>
                                        )}
                                        {location.pathname === '/admin' && pendingWelcomes === 0 && (
                                            <ChevronRight className="ml-auto h-4 w-4" />
                                        )}
                                    </Link>
                                    <Link
                                        to="/admin/soporte"
                                        onClick={() => setSidebarOpen(false)}
                                        className={cn(
                                            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors relative',
                                            location.pathname === '/admin/soporte'
                                                ? 'bg-slate-900 text-white'
                                                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                                        )}
                                        data-testid="admin-soporte-nav-btn"
                                    >
                                        <span className="relative inline-flex">
                                            <LifeBuoy className="h-5 w-5" />
                                            {openTickets > 0 && (
                                                <span
                                                    className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white animate-pulse"
                                                    data-testid="admin-soporte-pending-dot"
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </span>
                                        Soporte
                                        {openTickets > 0 && (
                                            <span
                                                className={cn(
                                                    'ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold',
                                                    location.pathname === '/admin/soporte'
                                                        ? 'bg-rose-500 text-white'
                                                        : 'bg-rose-100 text-rose-700'
                                                )}
                                                data-testid="admin-soporte-pending-badge"
                                                title={`${openTickets} ticket(s) de soporte abierto(s)`}
                                            >
                                                {openTickets > 99 ? '99+' : openTickets}
                                            </span>
                                        )}
                                        {location.pathname === '/admin/soporte' && openTickets === 0 && (
                                            <ChevronRight className="ml-auto h-4 w-4" />
                                        )}
                                    </Link>
                                </>
                            )}
                        </nav>
                    </ScrollArea>

                    {/* User section */}
                    <div className="border-t border-slate-200 p-4">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    data-testid="user-menu-trigger"
                                    className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-slate-100"
                                >
                                    <Avatar className="h-9 w-9">
                                        <AvatarFallback className="bg-slate-200 text-slate-600 text-sm font-medium">
                                            {getInitials(user?.nombre)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 overflow-hidden">
                                        <p className="truncate text-sm font-medium text-slate-900">{user?.nombre}</p>
                                        <p className="truncate text-xs text-slate-500">{user?.email}</p>
                                    </div>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem onClick={() => navigate('/perfil')}>
                                    <User className="mr-2 h-4 w-4" />
                                    Mi Perfil
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="text-rose-600" data-testid="logout-btn">
                                    <LogOut className="mr-2 h-4 w-4" />
                                    Cerrar Sesión
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="lg:ml-64">
                {/* Top bar */}
                <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b border-slate-200 bg-white/95 backdrop-blur px-4 lg:px-6">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="lg:hidden"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        data-testid="mobile-menu-btn"
                    >
                        {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </Button>

                    <div className="flex-1" />

                    <Button
                        variant="default"
                        size="sm"
                        className="gap-2"
                        onClick={() => navigate('/chat')}
                        data-testid="quick-chat-btn"
                    >
                        <MessageSquare className="h-4 w-4" />
                        <span className="hidden sm:inline">Nueva Consulta</span>
                    </Button>
                </header>

                {/* Page content */}
                <main className="p-4 lg:p-6">
                    <div className="mb-4">
                        <TrialBanner />
                    </div>
                    {children}
                </main>
            </div>

            {/* Widget flotante de soporte */}
            <SoporteForm />
        </div>
    );
};
